<template>
  <!-- a body-ba tesszük: beágyazva a bezárása a szülő ablakot is bezárná -->
  <Teleport to="body">
    <div
      ref="modal"
      class="modal fade ot-modal"
      tabindex="-1"
      aria-hidden="true"
      aria-labelledby="operationTreeTitle"
    >
      <div class="modal-dialog ot-dialog" :class="{ 'is-side': sideOpen }">
        <div class="modal-content ot">
          <div class="ot-head">
            <img v-if="image" :src="image" :alt="serial" class="ot-shot" />
            <div class="ot-head-main">
              <h5 id="operationTreeTitle">
                {{ $t("operationBoard.treeTitle") }} — {{ serial }}
              </h5>
              <p>
                {{
                  editable
                    ? $t("operationBoard.dragHint")
                    : $t("operationBoard.treeHint")
                }}
              </p>
            </div>
            <div class="ot-stats">
              <div class="ot-stat">
                <span>{{ $t("operationBoard.sumTime") }}</span>
                <b>{{ minutes(sumTime) }}</b>
              </div>
              <div class="ot-stat ot-stat--flow">
                <span>{{ $t("operationBoard.realTime") }}</span>
                <b>{{ minutes(realTime) }}</b>
              </div>
              <div class="ot-stat ot-stat--branch">
                <span>{{ $t("operationBoard.diffTime") }}</span>
                <b>{{ minutes(sumTime - realTime) }}</b>
              </div>
              <div class="ot-stat">
                <span>{{ $t("operationBoard.phases") }}</span>
                <b>{{ phases.length }} / {{ parallelCount }}⚡</b>
              </div>
            </div>
            <button
              type="button"
              class="btn-close"
              :aria-label="$t('operationBoard.close')"
              @click="hide"
            ></button>
          </div>

          <div class="ot-body">
            <div class="ot-canvas">
              <div class="ot-inner" :class="{ 'is-dnd': dragging }">
                <div class="ot-start">
                  {{ $t("operationBoard.treeStart") }}
                </div>

                <template v-for="(ph, pi) in phases" :key="ph.key">
                  <div class="ot-zone ot-zone--phase">
                    <span class="ot-zone-label">{{
                      $t("operationBoard.dropPhase")
                    }}</span>
                    <draggable
                      class="ot-zone-drop"
                      :list="bucket"
                      :group="dragGroup"
                      :disabled="!editable"
                      item-key="key"
                      v-bind="dragOptions"
                      @start="dragging = true"
                      @end="dragging = false"
                      @change="onZone('phase', ph, $event)"
                    >
                      <template #item><span></span></template>
                    </draggable>
                  </div>

                  <div class="ot-spine"></div>

                  <div class="ot-phase">
                    <div
                      class="ot-phase-label"
                      :class="{ 'is-solo': ph.threads.length < 2 }"
                    >
                      <template v-if="ph.threads.length > 1">
                        <span class="ot-bolt">⚡</span>
                        {{ pi + 1 }}. {{ $t("operationBoard.phase") }} —
                        {{ ph.threads.length }}
                        {{ $t("operationBoard.threadsAtOnce") }}
                      </template>
                      <template v-else>
                        {{ pi + 1 }}. {{ $t("operationBoard.phase") }}
                      </template>
                    </div>

                    <div
                      class="ot-threads"
                      :class="{ 'is-par': ph.threads.length > 1 }"
                      :style="fanStyle(ph)"
                    >
                      <template v-if="ph.threads.length > 1">
                        <i class="ot-fan ot-fan--top"></i>
                        <i class="ot-fan ot-fan--bot"></i>
                      </template>

                      <div
                        v-for="th in ph.threads"
                        :key="th.key"
                        class="ot-thread"
                        :class="{
                          'is-solo': ph.threads.length < 2,
                          'is-crit': th.time === ph.longest,
                        }"
                      >
                        <div class="ot-chip-row" v-if="ph.hasGroup">
                          <span
                            class="ot-group-chip"
                            v-if="th.steps.length > 1"
                          >
                            {{ $t("operationBoard.group") }} ·
                            {{ th.steps.length }}
                            {{ $t("operationBoard.step") }} · {{ th.time }} mp
                            <button
                              v-if="editable"
                              type="button"
                              class="ot-ungroup"
                              :title="$t('operationBoard.ungroup')"
                              @click="ungroup(th)"
                            >
                              ⤬
                            </button>
                          </span>
                        </div>
                        <draggable
                          class="ot-thread-in"
                          :list="th.steps"
                          :group="dragGroup"
                          :disabled="!editable"
                          item-key="key"
                          v-bind="dragOptions"
                          @start="dragging = true"
                          @end="dragging = false"
                          @change="onChange"
                        >
                          <template #item="{ element: s, index: si }">
                            <div
                              class="ot-node"
                              :class="{
                                'is-crit':
                                  th.time === ph.longest &&
                                  ph.threads.length > 1,
                              }"
                              role="button"
                              tabindex="0"
                              @click="pick(s)"
                            >
                              <span class="ot-no">{{
                                th.steps.length > 1
                                  ? pi + 1 + "/" + (si + 1)
                                  : pi + 1
                              }}</span>
                              <span class="ot-node-main">
                                <span class="ot-node-op">{{ opName(s) }}</span>
                                <span
                                  class="ot-node-desc"
                                  v-if="s.description"
                                  >{{ s.description }}</span
                                >
                                <span class="ot-node-time"
                                  >{{ s.time || 0 }} mp</span
                                >
                              </span>
                              <i
                                class="ot-dot"
                                :class="{ 'is-none': !s.videos_count }"
                              ></i>
                            </div>
                          </template>
                        </draggable>
                        <span
                          class="ot-wait"
                          v-if="ph.threads.length > 1 && th.time < ph.longest"
                          >+{{ ph.longest - th.time }}
                          {{ $t("operationBoard.wait") }}</span
                        >
                      </div>

                      <div class="ot-zone">
                        <span class="ot-zone-label">{{
                          $t("operationBoard.dropParallel")
                        }}</span>
                        <draggable
                          class="ot-zone-drop"
                          :list="bucket"
                          :group="dragGroup"
                          :disabled="!editable"
                          item-key="key"
                          v-bind="dragOptions"
                          @start="dragging = true"
                          @end="dragging = false"
                          @change="onZone('parallel', ph, $event)"
                        >
                          <template #item><span></span></template>
                        </draggable>
                      </div>
                    </div>
                  </div>
                </template>

                <div class="ot-spine"></div>
                <div class="ot-start ot-start--end">
                  {{ $t("operationBoard.treeEnd") }}
                </div>
              </div>
            </div>

            <!-- a tábla szerkesztője ide költözik át (Teleport), ezért csak
                 a helyét tartjuk fenn — így nincs kódmásolat és az állapot
                 sem vész el -->
            <aside class="ot-side" v-show="sideOpen">
              <div class="ot-side-head">
                <span>{{ $t("operationBoard.editor") }}</span>
                <button
                  type="button"
                  class="ot-side-close"
                  :aria-label="$t('operationBoard.close')"
                  @click="closeSide"
                >
                  ×
                </button>
              </div>
              <div class="ot-side-port" ref="sidePort"></div>
            </aside>
          </div>

          <div class="ot-key">
            <span
              ><i class="ot-kline"></i> {{ $t("operationBoard.keyFlow") }}</span
            >
            <span
              ><i class="ot-kline ot-kline--branch"></i>
              {{ $t("operationBoard.keyBranch") }}</span
            >
            <span class="ot-key-branch">{{
              $t("operationBoard.keyWait")
            }}</span>
            <span
              ><i class="ot-dot"></i> {{ $t("operationBoard.keyVideo") }}</span
            >
            <span
              ><i class="ot-dot is-none"></i>
              {{ $t("operationBoard.keyNoVideo") }}</span
            >
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
const bootstrap = require("bootstrap");
import draggable from "vuedraggable";

/**
 * Gyártási fa: fentről lefelé az egymás UTÁN következő fázisok, ahol a törzs
 * szétágazik, ott a szálak EGYSZERRE futnak, majd visszatérnek.
 *
 * A húzás ugyanazzal a vuedraggable/SortableJS-szel megy, mint a régi műveleti
 * szerkesztőben, `forceFallback` móddal: így a böngésző natív drag API-ját
 * egyáltalán nem használjuk — az ragadt be korábban egy mozgatás után.
 *
 * A fa saját munkamodellt tart (`model`): minden SZÁL egy tömb, akkor is, ha
 * egyetlen lépés van benne. Így minden ejtőhely valódi tömb, amit a könyvtár
 * kezelni tud. Mentéskor a `serialize()` adja vissza a tábla formáját: az
 * egyelemű szálakból újra sima lépés lesz, a többeleműekből csoport.
 */
export default {
  name: "OperationTreeModal",
  components: { draggable },
  props: {
    steps: Array,
    operations: Object,
    serial: String,
    name: String,
    image: String,
    editable: Boolean,
  },
  emits: ["select", "update", "side"],
  data() {
    return {
      myModal: undefined,
      model: [],
      bucket: [],
      dragging: false,
      sideOpen: false,
      syncing: false,
      pendingSync: false,
      localKey: -1,
      dragGroup: { name: "otSteps", pull: true, put: true },
      dragOptions: {
        animation: 180,
        ghostClass: "ot-ghost",
        forceFallback: true,
        fallbackOnBody: true,
        scroll: true,
        scrollSensitivity: 120,
      },
    };
  },
  watch: {
    steps: {
      immediate: true,
      handler() {
        // amit mi magunk küldtünk fel, azt ne építsük újra magunk alatt
        if (!this.syncing) this.buildModel();
      },
    },
  },
  computed: {
    phases() {
      const result = [];
      this.model.forEach((t, i) => {
        const thread = {
          key: t.key,
          at: i,
          steps: t.steps,
          time: t.steps.reduce((a, s) => a + (Number(s.time) || 0), 0),
        };
        if (i === 0 || !t.parallel) {
          result.push({ key: "ph" + t.key, threads: [thread] });
        } else {
          result[result.length - 1].threads.push(thread);
        }
      });
      result.forEach((p) => {
        p.longest = Math.max(0, ...p.threads.map((t) => t.time));
        // ha a fázisban van csoport, minden szál kap címkesort, hogy a
        // kártyák egy vonalban kezdődjenek
        p.hasGroup = p.threads.some((t) => t.steps.length > 1);
      });
      return result;
    },
    sumTime() {
      return this.phases.reduce(
        (a, p) => a + p.threads.reduce((x, t) => x + t.time, 0),
        0
      );
    },
    realTime() {
      return this.phases.reduce((a, p) => a + p.longest, 0);
    },
    stepCount() {
      return this.phases.reduce(
        (a, p) => a + p.threads.reduce((x, t) => x + t.steps.length, 0),
        0
      );
    },
    parallelCount() {
      return this.phases.filter((p) => p.threads.length > 1).length;
    },
  },
  methods: {
    /** a tábla lépéseiből szálakat képez — minden szál valódi tömb */
    buildModel() {
      this.model = (this.steps || []).map((it) => ({
        key: this.localKey--,
        parallel: !!it.parallel,
        src: it.type === "group" ? it : null,
        steps: it.type === "group" ? it.steps.slice() : [it],
      }));
    },
    /** a csoport szétesik: minden lépése önálló, egymás utáni művelet lesz */
    ungroup(thread) {
      const at = this.model.findIndex((t) => t.key === thread.key);
      if (at < 0) return;
      const src = this.model[at];
      const parts = src.steps.map((step, i) => ({
        key: this.localKey--,
        parallel: i === 0 ? src.parallel : false,
        src: null,
        steps: [step],
      }));
      this.model.splice(at, 1, ...parts);
      this.onChange();
    },
    /** üres ejtőhelyre húzott lépésből új szál lesz */
    onZone(kind, phase, evt) {
      const el = evt?.added?.element;
      this.bucket = [];
      if (!el) return;
      const first = phase.threads[0].at;
      const last = phase.threads[phase.threads.length - 1].at;
      const at = kind === "parallel" ? last + 1 : first;
      this.model.splice(at, 0, {
        key: this.localKey--,
        parallel: kind === "parallel",
        src: null,
        steps: [el],
      });
      this.onChange();
    },
    /**
     * Egy ejtés két listát is érint (ahonnan kikerült, ahová bekerült), ezért
     * a véglegesítést a következő tickre halasztjuk: így egyetlen mentés lesz.
     */
    onChange() {
      if (this.pendingSync) return;
      this.pendingSync = true;
      this.$nextTick(() => {
        this.pendingSync = false;
        this.commit();
      });
    },
    commit() {
      this.model = this.model.filter((t) => t.steps.length);
      if (this.model.length) this.model[0].parallel = false;
      this.syncing = true;
      this.$emit("update", this.serialize());
      this.$nextTick(() => {
        this.syncing = false;
      });
    },
    /** vissza a tábla formájára: egyelemű szál = lépés, több = csoport */
    serialize() {
      const out = [];
      this.model.forEach((t) => {
        if (!t.steps.length) return;
        if (t.steps.length === 1) {
          const only = t.steps[0];
          only.parallel = !!t.parallel;
          out.push(only);
        } else {
          const group =
            t.src && t.src.type === "group" ? t.src : { type: "group" };
          group.key = t.src?.key ?? t.key;
          group.type = "group";
          group.parallel = !!t.parallel;
          group.steps = t.steps.slice();
          out.push(group);
        }
      });
      if (out.length) out[0].parallel = false;
      return out;
    },
    opName(step) {
      return this.operations?.[step.manufacturing_operation_id] || "–";
    },
    minutes(sec) {
      return (sec / 60).toFixed(1) + " " + this.$t("operationBoard.minute");
    },
    /** az elágazás vonala az első és az utolsó szál közepéig ér */
    fanStyle(phase) {
      const half = 50 / phase.threads.length;
      return { "--ot-fan-inset": half + "%" };
    },
    /** műveletre kattintva nyílik az oldalsó szerkesztő */
    pick(step) {
      this.$emit("select", step);
      this.setSide(true);
    },
    closeSide() {
      this.setSide(false);
    },
    /**
     * A szerkesztő helyét MAGÁT adjuk át, nem azonosítót: a termékek listája
     * soronként példányosítja a fát, így az azonosító nem lenne egyedi, és a
     * szerkesztő egy másik termék rejtett fájába költözne.
     */
    setSide(open) {
      if (this.sideOpen === open) return;
      this.sideOpen = open;
      this.$emit("side", open ? this.$refs.sidePort || null : null);
    },
    hide() {
      if (this.myModal) this.myModal.hide();
    },
    showModal() {
      if (this.myModal === undefined) {
        this.myModal = new bootstrap.Modal(this.$refs.modal, {
          backdrop: "static",
        });
        // bezáráskor a szerkesztő visszaköltözik a táblára
        this.$refs.modal.addEventListener("hidden.bs.modal", () => {
          this.setSide(false);
        });
      }
      this.setSide(false);
      this.myModal.show();
    },
  },
};
</script>

<style scoped>
.ot-modal {
  /* a mögötte lévő oldal elhomályosodik */
  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);
}
.ot-dialog {
  max-width: 1180px;
  margin: 26px auto;
  transition: max-width 0.2s;
}
.ot-dialog.is-side {
  max-width: min(1620px, calc(100vw - 40px));
}
.ot {
  border: 0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 30px 70px -30px rgba(20, 22, 34, 0.55);
  --ot-flow: #713ae8;
  --ot-branch: #c2761b;
  --ot-branch-soft: #fdf1e0;
  background: #f7f8fb;
}
.ot-shot {
  width: 46px;
  height: 58px;
  flex: none;
  border-radius: 10px;
  object-fit: cover;
  border: 1px solid var(--divider);
}
.ot-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex-wrap: wrap;
  padding: 15px 18px;
  border-bottom: 1px solid var(--divider);
  background: var(--white-color);
}
.ot-head-main h5 {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: var(--ot-flow);
}
.ot-head-main p {
  margin: 3px 0 0;
  font-size: 12px;
  color: var(--gray);
  max-width: 62ch;
}
.ot-stats {
  margin-left: auto;
  display: flex;
  gap: 9px;
  flex-wrap: wrap;
}
.ot-stat {
  min-width: 118px;
  padding: 7px 13px;
  border: 1px solid var(--divider);
  border-radius: 10px;
  background: var(--white-color);
  text-align: center;
}
.ot-stat span {
  display: block;
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--gray-light);
}
.ot-stat b {
  display: block;
  font-size: 18px;
  font-weight: 700;
}
.ot-stat--flow b {
  color: var(--ot-flow);
}
.ot-stat--branch b {
  color: var(--ot-branch);
}

.ot-body {
  display: flex;
  align-items: stretch;
  gap: 16px;
  padding: 26px 18px 30px;
  max-height: calc(100vh - 230px);
  overflow: hidden;
}
/* a fa görgethető felülete */
.ot-canvas {
  flex: 1;
  min-width: 0;
  overflow: auto;
}
.ot-inner {
  min-width: 640px;
  max-width: 980px;
  margin: 0 auto;
}
/* oldalsó szerkesztő: külön kártya, saját görgetéssel */
.ot-side {
  flex: 0 0 400px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 1px solid var(--divider);
  border-radius: 14px;
  background: var(--extra-light);
  overflow: hidden;
}
.ot-side-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--divider);
  background: var(--white-color);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--gray);
}
.ot-side-close {
  margin-left: auto;
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 7px;
  background: transparent;
  font-size: 19px;
  line-height: 1;
  color: var(--gray);
}
.ot-side-close:hover {
  background: var(--extra-light);
  color: var(--black);
}
.ot-side-port {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 12px;
}

.ot-start {
  display: block;
  width: max-content;
  margin: 0 auto;
  padding: 9px 22px;
  border-radius: 22px;
  background: var(--ot-flow);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.ot-start--end {
  background: var(--black);
}
.ot-spine {
  width: 3px;
  height: 26px;
  margin: -1px auto;
  border-radius: 2px;
  background: var(--ot-flow);
}

.ot-phase-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-bottom: 9px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ot-branch);
}
.ot-phase-label.is-solo {
  color: var(--gray-light);
  justify-content: center;
}

.ot-threads {
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 16px;
}
.ot-threads.is-par {
  --ot-top: 24px;
  --ot-bot: 38px;
  padding: var(--ot-top) 0 var(--ot-bot);
}
.ot-thread {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
}
.ot-thread.is-solo {
  flex: 0 0 auto;
  width: 350px;
  margin: 0 auto;
}
.ot-fan {
  position: absolute;
  left: var(--ot-fan-inset);
  right: var(--ot-fan-inset);
  height: 3px;
  border-radius: 2px;
  background: var(--ot-branch);
}
.ot-fan--top {
  top: 0;
}
.ot-fan--bot {
  bottom: 0;
}
.ot-fan::before,
.ot-fan::after {
  content: "";
  position: absolute;
  top: -3px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--ot-branch);
}
.ot-fan::before {
  left: -4px;
}
.ot-fan::after {
  right: -4px;
}
/* a szál függőleges vonala a felső és az alsó elágazásig ér, lekerekítve */
.ot-threads.is-par > .ot-thread::before {
  content: "";
  position: absolute;
  top: calc(var(--ot-top) * -1);
  bottom: calc(var(--ot-bot) * -1);
  left: 50%;
  width: 3px;
  margin-left: -1.5px;
  border-radius: 2px;
  background: var(--divider);
}
.ot-threads.is-par > .ot-thread.is-crit::before {
  background: var(--ot-flow);
}
.ot-thread-in {
  position: relative;
  z-index: 1;
}

.ot-chip-row {
  display: flex;
  align-items: flex-end;
  min-height: 30px;
}
.ot-ungroup {
  display: grid;
  place-items: center;
  width: 17px;
  height: 17px;
  margin: -2px -4px -2px 1px;
  padding: 0;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: inherit;
  font-size: 13px;
  line-height: 1;
}
.ot-ungroup:hover {
  background: var(--white-color);
  color: #b3261e;
}
.ot-group-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 3px 11px;
  border-radius: 7px;
  background: var(--extra-light);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--gray);
}

.ot-node {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) 10px;
  gap: 10px;
  align-items: center;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: 1px solid var(--divider);
  border-radius: 11px;
  background: var(--white-color);
}
.ot-node + .ot-node {
  margin-top: 8px;
}
.ot-node:hover {
  border-color: var(--ot-flow);
}
.ot-inner.is-dnd .ot-node,
.ot-node:active {
  cursor: grab;
}
/* a húzott elem helyén maradó rés */
.ot-ghost {
  opacity: 0.45;
  border-style: dashed !important;
  border-color: var(--ot-flow) !important;
  background: #eaf3fe !important;
}
/* Ejtőhelyek: mindig a DOM-ban vannak, de csak húzás közben nyílnak ki —
   így a fa nem lesz tele üres dobozokkal. */
.ot-zone {
  position: relative;
  flex: 0 0 0;
  width: 0;
  height: 0;
  min-height: 0;
  padding: 0;
  margin: 0;
  border: 0;
  overflow: hidden;
  opacity: 0;
  align-self: center;
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--gray);
  text-align: center;
  white-space: nowrap;
}
.ot-inner.is-dnd .ot-zone {
  flex: 0 0 132px;
  width: auto;
  height: auto;
  min-height: 64px;
  padding: 6px;
  border: 1px dashed var(--divider);
  border-radius: 10px;
  opacity: 1;
}
.ot-inner.is-dnd .ot-zone--phase {
  flex: none;
  width: 330px;
  min-height: 40px;
  margin: 4px auto;
}
.ot-zone-label {
  pointer-events: none;
}
/* a tényleges ejtőfelület kitölti a zónát */
.ot-zone-drop {
  position: absolute;
  inset: 0;
  border-radius: 10px;
}
.ot-inner.is-dnd .ot-zone:has(.ot-zone-drop > *) {
  border-style: solid;
  border-color: var(--ot-flow);
  background: #eaf3fe;
  color: var(--ot-flow);
}
/* az ejtőhelyen a jelölő kitölti a zónát — elrejteni nem szabad, abból
   olvassa ki a könyvtár, hova kerülne az elem */
.ot-zone-drop > .ot-ghost {
  position: absolute;
  inset: 0;
  margin: 0;
  padding: 0;
  border-radius: 10px;
}
.ot-node.is-crit {
  border-color: var(--ot-branch);
}
.ot-no {
  font-size: 13px;
  font-weight: 700;
  text-align: center;
  padding: 3px 0;
  border-radius: 7px;
  background: var(--extra-light);
  color: var(--gray);
}
.ot-node-main {
  min-width: 0;
}
.ot-node-op {
  display: block;
  font-size: 14px;
  font-weight: 700;
}
.ot-node-desc {
  display: block;
  font-size: 11px;
  color: var(--gray);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ot-node-time {
  display: block;
  font-size: 12px;
  color: var(--gray);
}
.ot-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #1a9d5f;
}
.ot-dot.is-none {
  background: var(--divider);
}

.ot-wait {
  position: absolute;
  left: 0;
  bottom: 10px;
  padding-right: 8px;
  background: #f7f8fb;
  font-size: 12px;
  font-weight: 600;
  color: var(--ot-branch);
}

.ot-key {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  padding: 12px 18px 16px;
  border-top: 1px solid var(--divider);
  font-size: 12px;
  color: var(--gray);
  background: var(--white-color);
}
.ot-key span {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}
.ot-kline {
  width: 18px;
  height: 3px;
  background: var(--ot-flow);
}
.ot-kline--branch {
  background: var(--ot-branch);
}
.ot-key-branch {
  color: var(--ot-branch);
  font-weight: 600;
}
</style>
